/**
 * The day assembly: which points one day has, and what each of them is.
 *
 * A module of its own because it has two callers on opposite sides of the wire, and it must be one
 * implementation. The planner runs it while generating; the arrange step runs it to draw the day the user
 * is laying out, before there is a trip and before there is a plan. Both then ask the same routing
 * provider about the same points, so the picture on screen and the itinerary later cannot disagree about
 * where a day begins, when it comes home, or which stop is last.
 *
 * Pure, like everything in this package: no DOM, no network, no clock.
 */

import type { PlanPlace, PlanStopKind } from "./plan.ts";
import { orderBySlot, slotRank } from "./slots.ts";

/**
 * The part of a place a day is assembled from.
 *
 * Structural rather than `PlanPlace`, so the two callers can pass what they have. The engine has full plan
 * places; the arrange step has what the places screen loaded. What matters is the other direction: a field
 * this function started *using* would break the caller that cannot supply it, at compile time, instead of
 * quietly reading `undefined` and drawing the wrong day.
 */
export interface DayPlace {
  id: string;
  name: string;
  lat: number;
  lon: number;
}

/**
 * One day's points, assembled from the arrangement and its two anchors.
 *
 * A function rather than a block inside the planning loop because the arrange step draws the same day:
 * the screen shows a map of what the user has laid out, and a second copy of this rule there would let
 * the picture and the plan disagree about where a day begins, when it comes home, and which stop is last.
 *
 * The order is the user's, once through `orderBySlot`: a day they timed is visited morning first and
 * night last, and a day they did not time comes out exactly as they arranged it, because that sort is
 * stable over equal ranks. The midday return is placed at the seam those slots make — before the first
 * afternoon stop — rather than given a time of its own.
 */
export function assembleDay<T extends DayPlace>(input: {
  /** Where the day starts: the previous night's lodging, or the trip's entry point on day 1. */
  from: { place: T; kind: "lodging" | "entry" } | null;
  /** Where it ends: that night's lodging. */
  to: T | null;
  /** The day's places, each with the time of day the user said it suits. */
  visits: ReadonlyArray<{ place: T; slot?: string | undefined }>;
  /** Whether the party drives back to its lodging at noon. */
  returnToLodging: boolean;
}): { points: T[]; kinds: PlanStopKind[] } {
  /*
   * Where the day comes back to rest.
   *
   * Tonight's lodging if there is one, otherwise this morning's — on a changeover day the party has already
   * handed the key back, so the room they return to is the new one. Null when the trip has no lodging at
   * all, and then a rest day cannot be honoured: there is nowhere to return to, and inventing one would put
   * a stop on a map at no coordinate.
   */
  const restAt = input.returnToLodging ? (input.to ?? input.from?.place ?? null) : null;

  const ordered = orderBySlot(input.visits, (visit) => visit.slot);
  const restIndex =
    restAt === null
      ? -1
      : (() => {
          const firstAfternoon = ordered.findIndex((visit) => slotRank(visit.slot) >= slotRank("chiều"));
          return firstAfternoon === -1 ? ordered.length : firstAfternoon;
        })();
  const visiting =
    restAt === null || restIndex === -1
      ? ordered
      : [...ordered.slice(0, restIndex), { place: restAt }, ...ordered.slice(restIndex)];

  const points = [
    ...(input.from ? [input.from.place] : []),
    ...visiting.map((visit) => visit.place),
    /*
     * The day still ends at the lodging, even though it paused there at noon.
     *
     * Dropped when the return is the last visit — a morning-only day rests and stays — because then the two
     * would be the same place back to back. Every other rest day comes home twice, which is what it does, and
     * the final drive back from the last place is a leg the day has to pay for.
     */
    ...(input.to !== null && !(restIndex === ordered.length && input.to.id === restAt?.id) ? [input.to] : []),
  ];

  /*
   * The kinds are read by **position**, not by id, and that is the whole reason this function returns them
   * beside the points.
   *
   * The party sleeps where it rests, so the same place appears twice in `points` with two different meanings
   * — keyed by id alone, the middle one would silently be labelled the day's end.
   */
  const kinds = points.map((point, index): PlanStopKind => {
    if (restAt !== null && restIndex !== -1 && index === (input.from ? 1 : 0) + restIndex) return "rest";
    if (input.from !== null && index === 0) return input.from.kind;
    if (input.to !== null && index === points.length - 1 && point.id === input.to.id) return "lodging";
    return "visit";
  });

  return { points, kinds };
}
